import { useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";

import { Grid, Typography, Container, Paper, Divider } from "@mui/material";
import { Button } from "@gpstrackit/react-controls";
import { NavLink } from "react-router-dom";
//Components
import DataView from 'Components/DataView';
import CreateEditPackage from 'Modules/packages/.components/createEditPackage';
import DeletePackage from 'Modules/packages/.components/deletePackage';
import AdvancedFilters from 'Modules/packages/views/main/.components/AdvancedFilters';
//Helpers
import { getDateFormatted, idsValidation } from 'Core/data/Helpers';

//Actions
import {
    packagesCreateEditOpenModal,
    packagesDeletePackageOpenModal,
} from 'Redux/actions';

//Notification
import { NotificationManager } from "Components/Notifications";

//Permission
import { HasPermission } from "Core/security"

const PackagesMain = (props) => {
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const [columns, setColumns] = useState([
        {
            key: "name",
            field: "name",
            title: messages['packages_main_name'],
            width: 200,
            show: true,
            sort: true,
            locked: true,
            resize: true,
            export: true,
            functionString: 'return row?.name',
            code: "packages_main_name",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/packages/overview/${item?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.name}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "projectId",
            sort: true,
            field: "projectId",
            title: messages['packages_main_project'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.projectId',
            code: "packages_main_project",
            render: (item) => {
                return <span>{item?.projectId}</span>
            }
        },
        {
            key: "assignedCount",
            field: "assignedCount",
            title: messages['packages_main_assigned'],
            width: 350,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.assignedCount',
            code: "packages_main_assigned",
            render: (item) => {
                return <span>{item?.assignedCount}</span>
            }
        },
        {
            key: "dealer_name",
            field: "dealer { id name }",
            title: messages['packages_main_dealer'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.dealer?.name',
            code: "packages_main_dealer",
            render: (item) => {
                return <span>{item?.dealer?.name}</span>
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['packages_main_created_at'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            sort: true,
            export: true,
            functionString: 'return row?.createdOn && (new Date(`${row?.createdOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "packages_main_created_at",
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime")}</span>
            }
        },
    ]);
    const [searchFields, setSearchFields] = useState(["name", "projectId", "dealer_name", "company_name"]);
    const [openPanel, setOpenPanel] = useState(false);
    const [platform, setPlatform] = useState(null);
    const [brand, setBrand] = useState(null);
    const [total, setTotal] = useState(0);
    const [selectedItems, setSelectedItems] = useState([]);

    const openModalEditPackage = useSelector(state => state.createEditPackageRedux.openModal);
    const openModalDeletePackage = useSelector(state => state.deletePackageRedux.openModal);
    const user = useSelector(state => state.securityRedux.user);

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={6}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['packages_main_title']} ({total})
                        </Typography>
                    </Grid>
                    <Grid item xs={6} className="main-view-buttons">
                        <Button
                            className="btn-dark-blue"
                            disabled={!HasPermission(user, "rol", "c")}
                            onClick={(e) => {
                                e.preventDefault();
                                dispatch(packagesCreateEditOpenModal());
                            }}
                        >
                            {messages['packages_main_add_packages']}
                        </Button>
                    </Grid>
                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataView
                        ref={tableRef}
                        exportName="packages"
                        dinamicQuery
                        name="pm-packages-list-v1"
                        entity="packages"
                        graphqlFunction="searchRoles"
                        graphqlSort="RoleSortInput"
                        graphqlConditions="RoleSearchInput"
                        customQuery="id"
                        columns={columns}
                        hideAllInPagination
                        //search
                        asyncSelect
                        searchConditions={["name", "projectId", "dealer_name", "company_name"]}
                        searchOptionValue="id"
                        searchOptionLabel="name"
                        customSearch={[{ fields: ["isCustom"], terms: [false], exact: true }, { fields: ["companyId"], terms: ['NULL'], exact: true }]}
                        onChangeConditions={(tags, conditions, total, newColumns, rows, sort) => {
                            //current conditions
                            let currentConditions = {}
                            let currentTags = {}
                            if (!conditions && tags) {
                                currentConditions = tableRef?.current?.conditions;
                                currentTags = tags;
                            } else {
                                currentConditions = conditions || [];
                                currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                                if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);

                                let validatePlatform = currentConditions && currentConditions?.find((tag) => tag?.fields && tag?.fields[0] === "projectId");
                                validatePlatform && setPlatform(validatePlatform?.terms[0] || null);

                                let validateBrand = currentConditions && currentConditions?.find((tag) => tag?.fields && tag?.fields[0] === "dealerId");
                                validateBrand && setBrand(validateBrand?.terms[0] || null);
                            }

                            currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'name' && item?.fields[0] !== 'isCustom' && item?.fields[0] !== 'companyId');

                            let tagsById = currentTags.filter((item) => typeof item.id === "number");
                            if (tagsById?.length > 0) {
                                //New Items
                                let currentIds = tagsById.map((item) => (item.id));
                                if (currentIds.length > 0) {
                                    currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                                }
                            }
                            let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
                            if (tagsByLabel.length > 0) {
                                let currentLabels = tagsByLabel.map((item) => (item.name))
                                if (currentLabels.length > 0) {
                                    currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                                }
                            }

                            currentConditions && currentConditions.push({ fields: ["isCustom"], terms: [false], exact: true, conditionOperator: "AND" });
                            currentConditions && currentConditions.push({ fields: ["companyId"], terms: ['NULL'], exact: true, conditionOperator: "AND" });

                            if (!conditions && tags) tableRef?.current?.getData(null, null, null, currentConditions, newColumns);
                            else tableRef?.current?.getData(null, rows, sort, currentConditions);
                        }}
                        searchFields={searchFields}
                        onFilters={() => { setOpenPanel(!openPanel) }}
                        filterSelected={(platform || brand)}
                        topheight={200}
                        onChangeTotal={(total) => setTotal(total)}
                        noSplit
                        onChangeColumns={(items) => setColumns(items)}
                        tableButtons={["export", 'orderColumn']}
                        options={[
                            {
                                id: "edit-package",
                                label: messages['packages_main_edit'],
                                show: true,
                                disabled: !HasPermission(user, "rol", "u"),
                                single: true,
                                action: () => {
                                    dispatch(packagesCreateEditOpenModal(selectedItems[0].id))
                                }
                            },
                            {
                                id: "delete-package",
                                label: messages['delete'],
                                show: true,
                                disabled: !HasPermission(user, "rol", "d"),
                                single: true,
                                action: () => {
                                    dispatch(packagesDeletePackageOpenModal(selectedItems[0].id))
                                }
                            }
                        ]}
                        menuOptions={() => {
                            return [
                                messages['packages_main_edit'],
                                messages['delete'],
                            ]
                        }}
                        onClickMenu={(event, item) => {
                            if (event === messages['packages_main_edit']) {
                                if (HasPermission(user, "rol", "u")) {
                                    dispatch(packagesCreateEditOpenModal(item?.id))
                                } else {
                                    NotificationManager.warning(
                                        messages['common_error_permission'],
                                        messages['packages_main_edit'],
                                        5000,
                                    );
                                }
                            }
                            if (event === messages['delete']) {
                                if (HasPermission(user, "rol", "d")) {
                                    dispatch(packagesDeletePackageOpenModal(item?.id))
                                } else {
                                    NotificationManager.warning(
                                        messages['common_error_permission'],
                                        messages['delete'],
                                        5000,
                                    );
                                }
                            }
                        }}
                        onSelectedItems={(items) => setSelectedItems(items)}
                    />
                </Grid>
            </Paper>
            {openModalEditPackage && <CreateEditPackage tableRef={tableRef} />}
            {openModalDeletePackage && <DeletePackage tableRef={tableRef} />}
            {
                !openModalEditPackage && !openModalDeletePackage &&
                <AdvancedFilters
                    tableRef={tableRef}
                    setOpenPanel={setOpenPanel}
                    openPanel={openPanel}
                    platform={platform}
                    setPlatform={setPlatform}
                    brand={brand}
                    setBrand={setBrand}
                />
            }
        </Container>
    )
}

export default PackagesMain;